import { motion } from 'framer-motion'
import { isEmpty, partition } from 'lodash'
import React from 'react'
import styled from 'styled-components'
import { layout } from 'styled-system'
import { Container, Grid } from 'components/Layout'
import { HEADER_HEIGHT_DESKTOP } from 'constants/common'
import { COLOR_INTENT, MENU_CUBIC_BEZIER_EASE, SPACE, Z_INDEX } from 'Theme'
import { ANIMATION_VARIANT, DESKTOP_ANIMATION_DELAY } from '../constants'
import {
  DesktopSectionOfSubItemsWithImages as SectionOfSubItemsWithImages,
  type Section,
} from './DesktopSectionOfSubItemsWithImages'
import { DesktopSectionOfSubItemsWithoutImages as SectionOfSubItemsWithoutImages } from './DesktopSectionOfSubItemsWithoutImages'
import { type DesktopMenuProps } from './types'

const SECTION_GRID_TEMPLATE_COLUMNS = {
  WITHOUT_IMAGES: '1fr',
  WITH_IMAGES: '1fr 2fr',
}
const MENU_DROPDOWN_HEIGHT_ANIMATION_DURATION_IN_S = 0.4
const MENU_CONTENT_OPACITY_ANIMATION_DURATION_IN_S = 0.6

const DROP_DOWN_VARIANTS = {
  [ANIMATION_VARIANT.OPEN]: {
    overflowY: 'hidden',
    height: 'auto',
    display: 'block',
  },
  [ANIMATION_VARIANT.CLOSE]: {
    overflowY: 'hidden',
    height: 0,
    transitionEnd: {
      display: 'none',
    },
  },
}

const OPACITY_VARIANTS = {
  [ANIMATION_VARIANT.OPEN]: {
    opacity: 1,
  },
  [ANIMATION_VARIANT.CLOSE]: {
    opacity: 0,
  },
}

const Dropdown = styled(motion.div).withConfig({
  shouldForwardProp: (prop) => !['isVisible'].includes(prop),
})`
  overflow-x: hidden;
  background-color: ${COLOR_INTENT.MENU.BACKGROUND};
  position: absolute;
  left: 0;
  z-index: ${Z_INDEX.MENU};
  ${layout};
  border-top: 1px solid;
  border-top-color: ${({ isVisible }) =>
    isVisible ? COLOR_INTENT.HEADER.BORDER : 'transparent'};
  width: 100%;
  max-height: calc(100vh - ${HEADER_HEIGHT_DESKTOP});
`

export const DesktopMenu: React.FC<React.PWC<DesktopMenuProps>> = ({
  dropDownMenu,
  isVisible,
}) => {
  const [[sectionOfSubItemsWithImages], sectionsOfSubItemsWithoutImages] =
    partition(dropDownMenu, {
      subItemsWithImages: true,
    })

  return (
    <Dropdown
      variants={DROP_DOWN_VARIANTS}
      initial={ANIMATION_VARIANT.CLOSE}
      exit={ANIMATION_VARIANT.CLOSE}
      animate={isVisible ? ANIMATION_VARIANT.OPEN : ANIMATION_VARIANT.CLOSE}
      transition={{
        ...MENU_CUBIC_BEZIER_EASE,
        duration: MENU_DROPDOWN_HEIGHT_ANIMATION_DURATION_IN_S,
        ...(!isVisible && { delay: DESKTOP_ANIMATION_DELAY }),
      }}
      isVisible={isVisible}
    >
      <motion.div
        variants={OPACITY_VARIANTS}
        animate={isVisible ? ANIMATION_VARIANT.OPEN : ANIMATION_VARIANT.CLOSE}
        transition={{
          ease: 'linear',
          duration: MENU_CONTENT_OPACITY_ANIMATION_DURATION_IN_S,
          ...(!isVisible && { delay: DESKTOP_ANIMATION_DELAY }),
        }}
      >
        <Container pt={SPACE.PX_32} pb={SPACE.PX_40}>
          <Grid
            width="100%"
            gridTemplateColumns={
              !sectionOfSubItemsWithImages
                ? SECTION_GRID_TEMPLATE_COLUMNS.WITHOUT_IMAGES
                : SECTION_GRID_TEMPLATE_COLUMNS.WITH_IMAGES
            }
          >
            {sectionOfSubItemsWithImages && (
              <SectionOfSubItemsWithImages
                section={sectionOfSubItemsWithImages as Section}
              />
            )}
            <SectionOfSubItemsWithoutImages
              section={sectionsOfSubItemsWithoutImages}
              isSectionOfSubItemsWithImagesShown={
                !isEmpty(sectionOfSubItemsWithImages)
              }
            />
          </Grid>
        </Container>
      </motion.div>
    </Dropdown>
  )
}
